/**
 * Lens coating ("Veredlung" / refinement) helpers.
 *
 * The configurator stores ONE coating code in `config.glass.refinement`. The coating UI shows
 * three tiers plus a blue-light toggle; this module maps between that UI model and the six
 * underlying SKU option codes so the data model stays untouched.
 */

export const COATING_TIERS = [
  {
    key: 'classic',
    base: 'LENS38',
    blue: 'LENS41',
    features: ['standardAntiReflective', 'hardScratchLayer', 'uvProtection']
  },
  {
    key: 'invisible',
    base: 'LENS39',
    blue: 'LENS40',
    features: ['superAntiReflective', 'hardScratchLayer', 'uvProtection']
  },
  {
    key: 'premium',
    base: 'LENS147',
    blue: 'LENS148',
    features: ['superAntiReflective', 'hardScratchLayerUv', 'ultraClean', 'dirtWaterRepellent']
  }
]

// Display-only fallback for the blue-light toggle when the selected tier's blue variant has no
// price in the options JSON. Normally the surcharge is the price difference between the tier's
// blue and base variants, so it always matches what the combined SKU bills.
export const BLUE_LIGHT_SURCHARGE = { CHF: 30, EUR: 20 }

// Old "Invisible UltraClean" codes, replaced by the Premium tier on the new UI.
export const LEGACY_COATING_MAP = { LENS139: 'LENS147', LENS140: 'LENS148' }

// Premium + Blue
export const DEFAULT_REFINEMENT = 'LENS148'

/**
 * Conditions where the default coating must be Classic (LENS38) instead of the premium default:
 * workspace lenses Standard A/B (LENS13/LENS14), index 1.74 (LENS30), bifocal prescription (LENS20).
 */
export function shouldUseLens38(config) {
  return (
    config?.glass?.lens === 'LENS13' ||
    config?.glass?.lens === 'LENS14' ||
    config?.glass?.index === 'LENS30' ||
    config?.prescription?.type === 'LENS20'
  )
}

export function defaultRefinementFor(config) {
  return shouldUseLens38(config) ? 'LENS38' : DEFAULT_REFINEMENT
}

/**
 * @param {string} code - a coating SKU option code (LENS38, LENS41, …, incl. legacy LENS139/LENS140)
 * @returns {{ tierKey: string, blue: boolean } | null}
 */
export function resolveCoatingSelection(code) {
  if (!code) return null
  const resolved = LEGACY_COATING_MAP[code] ?? code
  for (const tier of COATING_TIERS) {
    if (tier.base === resolved) return { tierKey: tier.key, blue: false }
    if (tier.blue === resolved) return { tierKey: tier.key, blue: true }
  }
  return null
}

/**
 * @param {string} tierKey
 * @param {boolean} blue
 * @param {Set<string>} availableIds - codes currently offered by `getAvailableRefinements`
 * @returns {string | null} the code to store; falls back to the base variant when blue is unavailable
 */
export function resolveCoatingCode(tierKey, blue, availableIds) {
  const tier = COATING_TIERS.find((t) => t.key === tierKey)
  if (!tier) return null
  if (blue && availableIds.has(tier.blue)) return tier.blue
  return tier.base
}

export function mapLegacyCoating(id) {
  return LEGACY_COATING_MAP[id] ?? id
}

const BLUE_LIGHT_FILTER_NAME = {
  de: 'Blaulichtfilter',
  en: 'Blue light filter',
  fr: 'Filtre lumière bleue'
}

/**
 * The supplier sells finish + blue light filter as one SKU option, but the invoice shows them as
 * two lines. Stores the filter's share on the line item option so the template can split it:
 * finish price = option total - blue_light_filter_price.
 *
 * @param {object} option - the selected coating option (with `price[currency].price`)
 * @param {object[]} refinements - all coating options, to look up the tier's base variant
 * @param {string} currency
 * @returns {object | null} line item option metadata, or null when the coating has no filter
 */
export function blueLightFilterMetadata(option, refinements, currency) {
  // Never throws: without metadata the invoice and order details show the combined line.
  const selection = resolveCoatingSelection(option?.id)
  if (!selection?.blue) return null
  const tier = COATING_TIERS.find((t) => t.key === selection.tierKey)
  const base = refinements?.find((item) => item.id === tier?.base)
  const total = option?.price?.[currency]?.price
  const basePrice = base?.price?.[currency]?.price
  if (!base?.name || typeof total !== 'number' || typeof basePrice !== 'number') return null
  const filterPrice = total - basePrice
  if (filterPrice <= 0) return null
  return {
    finish_name: base.name,
    blue_light_filter_name: BLUE_LIGHT_FILTER_NAME,
    blue_light_filter_price: filterPrice
  }
}
